import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ArrowDown, ArrowRight, Fuel, Info, MapPin, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getMeta, getMonthlyComponents, getMultiSeries, getOverview, getSeries } from '../api/endpoints';
import { useFilters } from '../app/FiltersContext';
import AnnualFuelComparisonChart from '../components/charts/AnnualFuelComparisonChart';
import PriceCompositionChart from '../components/charts/PriceCompositionChart';
import PriceTrendChart from '../components/charts/PriceTrendChart';
import EmptyState from '../components/feedback/EmptyState';
import ErrorState from '../components/feedback/ErrorState';
import QuickFuelControls from '../components/filters/QuickFuelControls';
import PriceChangePill from '../components/public/PriceChangePill';
import ShareButton from '../components/public/ShareButton';
import TonalLink from '../components/public/TonalLink';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { useAsync } from '../hooks/useAsync';
import { formatDate, formatPrice } from '../utils/formatters';

const fuelNames = { BENZINA: 'Benzina', GASOLIO: 'Diesel', GPL: 'GPL', METANO: 'Metano' };

function PriceFact({ label, value, helper }) {
  return (
    <div className="min-w-0 rounded-[22px] bg-md-surface-container-low px-4 py-4">
      <div className="text-[11px] font-medium text-md-on-surface-variant">{label}</div>
      <div className="numbers mt-1 break-words text-xl font-bold tracking-[-0.02em] text-md-on-surface">{value}</div>
      {helper && <div className="mt-1 text-[11px] leading-4 text-md-on-surface-variant">{helper}</div>}
    </div>
  );
}

export default function HomePage() {
  const { fuel, service, territory, from, to } = useFilters();
  const [allMetric, setAllMetric] = useState('median_price');
  const [compositionFuel, setCompositionFuel] = useState(['BENZINA', 'GASOLIO'].includes(fuel) ? fuel : 'BENZINA');
  const params = { fuel, service, geo_level: territory.type, geo_code: territory.code };
  const meta = useAsync(getMeta, []);
  const overview = useAsync(() => getOverview(params), [fuel, service, territory.type, territory.code]);
  const series = useAsync(() => getSeries({ ...params, from, to }), [fuel, service, territory.type, territory.code, from, to]);
  const multiSeries = useAsync(() => getMultiSeries({ geo_level: territory.type, geo_code: territory.code, from, to }), [territory.type, territory.code, from, to]);
  const composition = useAsync(() => getMonthlyComponents({ fuel: compositionFuel, from, to }), [compositionFuel, from, to]);
  const current = overview.data?.current;
  const change = overview.data?.changes?.['365'];
  const changeValue = change ? Number(change.percent) : null;
  const fuelLabel = fuelNames[fuel] || fuel;
  const compositionItems = composition.data?.items || [];
  const latestComposition = compositionItems[compositionItems.length - 1];

  useEffect(() => {
    if (['BENZINA', 'GASOLIO'].includes(fuel)) setCompositionFuel(fuel);
  }, [fuel]);

  return (
    <div className="min-w-0 overflow-x-clip">
      <section className="relative">
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-7 sm:px-6 sm:pb-14 sm:pt-12">
          <div className="relative min-w-0 overflow-hidden rounded-[42px_42px_18px_42px] bg-md-secondary text-md-on-secondary">
            <div aria-hidden="true" className="absolute right-0 top-0 h-3 w-40 bg-md-primary sm:w-64" />
            <div aria-hidden="true" className="absolute bottom-0 left-[55%] top-0 hidden w-px bg-white/15 lg:block" />

            <div className="relative grid min-w-0 gap-10 px-6 pb-14 pt-9 sm:px-10 sm:pb-16 sm:pt-12 lg:grid-cols-[minmax(0,1.08fr)_minmax(21rem,.92fr)] lg:items-center lg:gap-14 lg:px-14 lg:pb-20 lg:pt-16">
              <div className="min-w-0">
                <h1 className="max-w-3xl text-[2.7rem] font-medium leading-[.98] tracking-[-0.045em] text-md-on-secondary sm:text-[4rem] lg:text-[4.65rem]">
                  I prezzi alla pompa, resi leggibili.
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-7 text-md-on-secondary/85 sm:text-lg sm:leading-8">
                  Seguiamo ogni giorno le rilevazioni MIMIT e mostriamo come cambiano i prezzi nei territori e nei singoli impianti.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild className="bg-md-primary text-md-on-primary hover:bg-md-primary/90">
                    <a href="#andamento">Esplora i dati <ArrowDown className="ml-2 h-4 w-4" /></a>
                  </Button>
                  <Button asChild variant="ghost" className="text-md-on-secondary hover:bg-white/10 hover:text-md-on-secondary">
                    <Link to="/metodologia">Come leggiamo i dati <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </div>
              </div>

              <article className="relative min-w-0 rounded-[34px_34px_12px_34px] bg-md-surface p-5 text-md-on-surface shadow-md2 sm:p-7">
                <div className="flex items-start justify-between gap-4 border-b border-md-outline-variant pb-4">
                  <div>
                    <div className="text-[16px] font-bold uppercase text-md-secondary">La tua lettura</div>
                    <div className="text-xs text-md-on-surface-variant">Il riepilogo segue i filtri qui sotto.</div>
                  </div>
                  <ShareButton />
                </div>

                <div className="py-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-md-secondary-container px-3 py-1.5 text-xs font-bold text-md-on-secondary-container">{service === 'self' ? 'Self-service' : 'Servito'}</span>
                    <span className="text-xs font-medium text-md-on-surface-variant">{territory.type === 'country' ? 'Quadro nazionale' : 'Ambito territoriale'}</span>
                  </div>
                  <h2 className="mt-4 break-words text-[2.6rem] font-medium leading-none tracking-[-0.045em] text-md-on-surface sm:text-[3.35rem]">{fuelLabel}</h2>
                  <div className="mt-2 break-words text-xl font-medium leading-tight text-md-secondary sm:text-2xl">{territory.name}</div>
                </div>

                <div className="rounded-[26px_26px_9px_26px] bg-md-primary-container p-5 text-md-on-primary-container">
                  <div className="text-xs font-medium">Mediana attuale</div>
                  <div className="numbers mt-2 break-words text-[2.8rem] font-bold leading-none tracking-[-0.055em] min-[390px]:text-[3.35rem]">{formatPrice(current?.median_price, fuel)}</div>
                  <div className="mt-4"><PriceChangePill value={changeValue} suffix="in 12 mesi" /></div>
                </div>

                <div className="mt-4 flex items-center justify-between gap-4 text-[11px] font-medium text-md-on-surface-variant">
                  <span>Fonte: MIMIT</span>
                  <span className="numbers text-right">Ultimo dato {meta.data?.latest_date ? formatDate(meta.data.latest_date) : '—'}</span>
                </div>
              </article>
            </div>
          </div>

          <div className="relative z-10 mx-2 -mt-7 min-w-0 rounded-[32px_18px_32px_32px] bg-md-surface p-5 shadow-md2 sm:mx-7 sm:-mt-9 sm:p-7">
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[16px] bg-md-primary-container text-md-on-primary-container"><Fuel className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <h2 className="text-lg font-medium text-md-on-surface">Imposta la tua lettura</h2>
                  <p className="text-xs leading-5 text-md-on-surface-variant">Carburante, modalità e territorio aggiornano il bollettino e tutti i grafici.</p>
                </div>
              </div>
              <div aria-live="polite" className="min-w-0 rounded-[20px_20px_7px_20px] bg-md-secondary-container px-4 py-3 text-md-on-secondary-container sm:max-w-[19rem] sm:text-right">
                <div className="text-[10px] font-bold uppercase tracking-[.1em]">Selezione attiva</div>
                <div className="mt-1 truncate text-base font-bold">{fuelLabel} · {service === 'self' ? 'Self-service' : 'Servito'}</div>
                <div className="truncate text-xs font-medium">{territory.name}</div>
              </div>
            </div>
            <QuickFuelControls horizontal />
          </div>
        </div>
      </section>

      <section id="andamento" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-7 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="mt-4 max-w-2xl text-[2rem] font-medium leading-tight tracking-[-0.03em] text-md-on-surface sm:text-[2.65rem]">Da un anno fa a oggi, giornata per giornata.</h2>
          </div>
          <div className="flex items-center gap-2 text-xs font-medium text-md-on-surface-variant">
            <span className="h-3 w-3 rounded-full bg-md-primary" /> {fuelLabel} · {service === 'self' ? 'Self' : 'Servito'}
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-[40px_40px_16px_40px] bg-md-secondary-container p-3 sm:p-6">
          <div className="grid min-w-0 gap-2 px-2 pb-3 min-[480px]:grid-cols-3 sm:max-w-2xl sm:px-0">
            <PriceFact label="Media oggi" value={formatPrice(current?.average_price, fuel)} />
            <PriceFact label="Ultimo dato" value={meta.data?.latest_date ? formatDate(meta.data.latest_date) : '—'} />
            <PriceFact label="Periodo" value="12 mesi" helper={`${dayjs(from).format('DD/MM/YY')} → ${dayjs(to).format('DD/MM/YY')}`} />
          </div>
          <div className="rounded-[30px_30px_10px_30px] bg-md-surface p-1 sm:p-4">
            {series.loading ? <Skeleton className="h-[360px] rounded-[28px] bg-md-surface-container" /> : series.error ? <ErrorState error={series.error} /> : <PriceTrendChart series={series.data?.items || []} height={380} showRange={false} metric="median_price" fuel={fuel} />}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_19rem] lg:items-end">
          <div>
            <h2 className="mt-4 max-w-2xl text-[2rem] font-medium leading-tight tracking-[-0.03em] text-md-on-surface sm:text-[2.65rem]">Com’è composto il prezzo</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-md-on-surface-variant">La statistica nazionale mensile separa il prezzo self-service in prezzo netto, accisa e IVA. Le tre componenti formano il prezzo finale alla pompa.</p>
          </div>
          <div>
            <div className="mb-2 text-xs font-medium text-md-on-surface-variant">Carburante nazionale self-service</div>
            <ToggleGroup type="single" value={compositionFuel} onValueChange={value => value && setCompositionFuel(value)} className="grid grid-cols-2 gap-2">
              <ToggleGroupItem value="BENZINA" className="h-11 border-0 bg-md-surface-container px-4 text-md-on-surface-variant hover:bg-md-secondary-container hover:text-md-on-secondary-container data-[state=on]:bg-md-secondary data-[state=on]:text-md-on-secondary">Benzina</ToggleGroupItem>
              <ToggleGroupItem value="GASOLIO" className="h-11 border-0 bg-md-surface-container px-4 text-md-on-surface-variant hover:bg-md-secondary-container hover:text-md-on-secondary-container data-[state=on]:bg-md-secondary data-[state=on]:text-md-on-secondary">Gasolio</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <div className="mt-6 min-w-0 overflow-hidden rounded-[40px_16px_40px_40px] bg-md-secondary-container p-3 sm:p-6">
          {latestComposition && (
            <div className="grid min-w-0 grid-cols-2 gap-2 px-2 pb-3 lg:grid-cols-4 lg:px-0">
              <PriceFact label="Prezzo finale" value={formatPrice(latestComposition.gross)} helper={dayjs(`${latestComposition.month}-01`).locale('it').format('MMMM YYYY')} />
              <PriceFact label="Netto" value={formatPrice(latestComposition.net)} />
              <PriceFact label="Accisa" value={formatPrice(latestComposition.excise)} />
              <PriceFact label="IVA" value={formatPrice(latestComposition.vat)} />
            </div>
          )}
          <div className="min-w-0 rounded-[30px_30px_10px_30px] bg-md-surface p-1 sm:p-4">
            {composition.loading ? <Skeleton className="h-[420px] rounded-[28px] bg-md-surface-container" /> : composition.error ? <ErrorState error={composition.error} onRetry={composition.reload} /> : compositionItems.length === 0 ? <EmptyState title="Serie MASE non ancora disponibile" text="Esegui l’import mensile MASE/SISEN per popolare la composizione nazionale del prezzo." /> : <PriceCompositionChart items={compositionItems} height={420} />}
          </div>
          <div className="grid gap-3 px-2 pb-2 pt-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-1">
            <div className="flex min-w-0 items-start gap-3 text-sm leading-6 text-md-on-secondary-container">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <p><b>Netto non significa petrolio.</b> È il prezzo prima delle imposte e comprende anche raffinazione, logistica, distribuzione e margini della filiera.</p>
            </div>
            <div className="text-xs font-medium text-md-on-secondary-container">Fonte: MASE – SISEN{composition.data?.source_updated_at ? ` · aggiornamento ${formatDate(composition.data.source_updated_at)}` : ''}</div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="mt-4 max-w-2xl text-[2rem] font-medium leading-tight tracking-[-0.03em] text-md-on-surface sm:text-[2.65rem]">Benzina, diesel, GPL e metano: chi si è mosso di più?</h2>
          </div>
          <Tabs value={allMetric} onValueChange={setAllMetric}>
            <TabsList className="bg-md-surface-container">
              <TabsTrigger value="average_price" className="data-[state=active]:bg-md-primary data-[state=active]:text-md-on-primary">Media</TabsTrigger>
              <TabsTrigger value="median_price" className="data-[state=active]:bg-md-primary data-[state=active]:text-md-on-primary">Mediana</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="mt-6 overflow-hidden rounded-[40px_16px_40px_40px] bg-md-tertiary-container p-3 sm:p-6">
          <div className="rounded-[30px_30px_10px_30px] bg-md-surface p-1 sm:p-4">
            {multiSeries.loading ? <Skeleton className="h-[420px] rounded-[28px] bg-md-surface-container" /> : multiSeries.error ? <ErrorState error={multiSeries.error} /> : <AnnualFuelComparisonChart items={multiSeries.data?.items || []} height={430} metric={allMetric} normalize />}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="mb-6 max-w-2xl">
          <h2 className="mt-4 text-[2rem] font-medium leading-tight tracking-[-0.03em] text-md-on-surface sm:text-[2.65rem]">Dal dato nazionale fino al singolo distributore.</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <TonalLink to="/confronta" title="Italia, regione, provincia, comune" description="Metti fino a cinque aree sullo stesso grafico giornaliero e confronta l’andamento." icon={Scale} tone="secondary" />
          <TonalLink to="/mappa" title="Apri un impianto e guarda il suo anno" description="Ogni modalità di rifornimento ha il proprio storico, con confronto rispetto alle medie territoriali." icon={MapPin} tone="tertiary" />
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6 sm:pb-16">
        <div className="grid overflow-hidden rounded-[38px_38px_14px_38px] bg-md-primary text-md-on-primary md:grid-cols-[1fr_auto]">
          <div className="p-6 sm:p-8">
            <h2 className="mt-4 max-w-2xl text-2xl font-medium leading-tight tracking-[-0.025em] sm:text-3xl">Media, mediana e prezzo del singolo impianto non sono la stessa cosa.</h2>
            <p className="mt-3 max-w-2xl text-sm leading-6">Le statistiche territoriali sono calcolate sui prezzi comunicati dagli impianti e non sono ponderate per i litri venduti.</p>
          </div>
          <div className="flex items-end justify-end p-6 sm:p-8">
            <Button asChild variant="secondary" className="bg-md-surface text-md-on-surface hover:bg-md-surface-container">
              <Link to="/metodologia">Come leggere i dati <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
